'use client';

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { useRateLimit } from './rate-limit-provider';
import { installMiddlewareRateLimitInterceptor } from '@/lib/middleware-rate-limit';
import { isAnyRateLimit, nextRetryDelay, shouldRetry } from './query-retry-policy';

// Install the fetch interceptor at module load so any client-side
// fetch — even ones that fire before the first React render —
// gets the MiddlewareRateLimitError discrimination.
installMiddlewareRateLimitInterceptor();

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const { reportRateLimit } = useRateLimit();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            // Surface the overlay for any 429 — both our middleware
            // backpressure and genuine upstream rate limits — so the
            // user knows the app is throttling itself.
            if (isAnyRateLimit(error)) {
              reportRateLimit();
            }
          },
        }),
        defaultOptions: {
          queries: {
            // Default stale time of 30 seconds
            staleTime: 30 * 1000,
            // Refetch on window focus
            refetchOnWindowFocus: true,
            // Retry policy lives in `./query-retry-policy.ts` so it
            // can be unit-tested in isolation. Key behaviour:
            //   • MiddlewareRateLimitError → no retry (our own
            //     rpc-proxy/SDK/DDoS backstop; bucket refills at the
            //     minute boundary, retrying just amplifies).
            //   • Upstream 429 (envio / RPC) → no retry. Retrying
            //     upstream 429s is counterproductive — it pushes N
            //     downstream fetches into the same refusal window.
            //     The next `refetchInterval` picks the data up
            //     cleanly, and `placeholderData: (prev) => prev`
            //     keeps the last-known value on screen meanwhile.
            //   • Everything else → at most one retry with ~1s delay.
            retry: shouldRetry,
            retryDelay: nextRetryDelay,
            // Keep previous data while fetching new data
            placeholderData: (prev: unknown) => prev,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
